import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Layout } from "../../components/Layout";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";

// Import the tab components (make sure these paths are correct)
import AppointmentHeader from "./AppointmentHeader";
import ClientInfoCard from "./ClientInfoCard";
import OverviewTab from "./OverviewTab";
import TimeTab from "./TimeTab";
import EquipmentTab from "./EquipmentTab";
import FilesTab from "./Files/FilesTab";
import SignatureModal from "../../components/SignatureModal";
import { FormattedMessage, useIntl } from "react-intl";
// Import constants
import {
  APPOINTMENT_STATUSES,
  INVENTORY_TYPE_LABELS,
} from "../../lib/constants";

// Import API and actions
import {
  GetAppointmentDetailsAPI,
  updateAppointmentStatusAPI,
  addOrUpdateTechnicianTimeRecord,
  deleteTechnicianTimeRecordAPI,
  getProductsServicesAPI,
} from "./appointmentAPI";
import {
  setActiveTab,
  setWorkNotes,
  setSignature,
  addTimeRecord,
  removeTimeRecord,
  addEquipmentUsage,
  removeEquipmentUsage,
  addUploadedFiles,
  removeUploadedFile,
  addOrUpdateFieldStatus,
  removeFieldStatus,
} from "./appointmentSlice";
import { useToast } from "../../hooks/use-toast";

const PREDEFINED_EQUIPMENT = [
  { name: "Drill", type: "tool" },
  { name: "Wrench Set", type: "tool" },
  { name: "Water Heater", type: "appliance" },
  { name: "Washing Machine", type: "appliance" },
  { name: "PVC Pipe", type: "plumbing" },
  { name: "Copper Fitting", type: "plumbing" },
  { name: "Wire Nuts", type: "electrical" },
  { name: "Circuit Breaker", type: "electrical" },
  { name: "Screws", type: "other" },
  { name: "Sealant", type: "other" },
];

function AppointmentDetailPage({ appointmentId, onNavigate }) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { fieldStatus } = useSelector((state) => state.appointment);
  const { toast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);
  const inventory = useSelector(
    (state) => state.appointment.inventoryItems.items,
  );
  const appointmentItems = useSelector(
    (state) => state.appointment.currentAppointment?.appointmentItems,
  );
  // Local state for forms
  const [newTimeRecord, setNewTimeRecord] = useState({
    type: "travel",
    startTime: "",
    endTime: "",
    notes: "",
  });
  const [selectedEquipment, setSelectedEquipment] = useState("");
  const [equipmentQuantity, setEquipmentQuantity] = useState(1);
  const [equipmentNotes, setEquipmentNotes] = useState("");
  const [showSignatureModal, setShowSignatureModal] = useState(false);

  // Get state from Redux
  const {
    currentAppointment,
    appointmentLoading,
    appointmentError,
    activeTab,
    workNotes,
    signature,
    customerSignatureName,
    customerSignatureEmail,
    signatureTimestamp,
    timeRecords,
    equipmentUsage,
    uploadedFiles,
  } = useSelector((state) => state.appointment);

  // Load appointment details on component mount
  useEffect(() => {
    if (appointmentId) {
      dispatch(GetAppointmentDetailsAPI(appointmentId));
      dispatch(getProductsServicesAPI());
    }
  }, [dispatch, appointmentId]);

  // Handle tab change
  const handleTabChange = (newTab) => {
    dispatch(setActiveTab(newTab));
  };

  // Handle work notes update
  const handleSaveNotes = () => {
    if (appointmentId) {
      //  dispatch(UpdateAppointmentNotesAPI(appointmentId, workNotes));
    }
  };

  const handleStatusChange = (newStatus) => {
    if (isUpdating || !appointmentId) {
      return;
    }
    setIsUpdating(true);
    dispatch(updateAppointmentStatusAPI(appointmentId, newStatus))
      .then(() => {
        toast({
          title: "Status Updated",
          description: "The appointment status was changed successfully.",
          variant: "success",
        });
      })
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.Message ||
          error?.response?.data ||
          error?.message ||
          "An unexpected error occurred.";

        toast({
          title: "Update Failed",
          description: errorMessage,
          variant: "error",
        });
      })
      .finally(() => {
        setIsUpdating(false);
      });
  };

  // Time record actions
  const addTimeRecordHandler = () => {
    if (!newTimeRecord.startTime || !newTimeRecord.endTime) {
      alert("Please fill time fields");
      return;
    }

    const start = new Date(`2024-01-01T${newTimeRecord.startTime}`);
    const end = new Date(`2024-01-01T${newTimeRecord.endTime}`);
    const duration = (end.getTime() - start.getTime()) / (1000 * 60);

    if (duration <= 0) {
      alert("End time must be after start time");
      return;
    }

    const timeRecord = {
      id: Date.now().toString(),
      ...newTimeRecord,
      duration,
    };

    dispatch(addTimeRecord(timeRecord));
    setNewTimeRecord({ type: "travel", startTime: "", endTime: "", notes: "" });
  };

  const removeTimeRecordHandler = (id) => {
    dispatch(removeTimeRecord(id));
  };

  // Equipment actions
  const addEquipmentHandler = () => {
    if (!selectedEquipment) {
      alert("Select equipment");
      return;
    }

    const eq = PREDEFINED_EQUIPMENT.find((eq) => eq.name === selectedEquipment);
    const equipment = {
      id: Date.now().toString(),
      name: eq.name,
      type: eq.type,
      quantity: equipmentQuantity,
      notes: equipmentNotes,
    };

    dispatch(addEquipmentUsage(equipment));
    setSelectedEquipment("");
    setEquipmentQuantity(1);
    setEquipmentNotes("");
  };

  const removeEquipmentHandler = (id) => {
    dispatch(removeEquipmentUsage(id));
  };

  // File actions
  const handleFileUpload = (files) => {
    const newFiles = files.map((file) => ({
      id: Date.now() + Math.random(),
      filename: file.name,
      filePath: URL.createObjectURL(file),
      fileType: file.type,
    }));
    dispatch(addUploadedFiles(newFiles));
  };

  const removeFileHandler = (id) => {
    dispatch(removeUploadedFile(id));
  };

  const handleCall = () => {
    if (currentAppointment?.clientDetails?.phone) {
      // Clean phone number (remove "Cell " prefix if present)
      const cleanPhone = currentAppointment.clientDetails.phone.replace(
        /Cell\s+/,
        "",
      );
      window.open(`tel:${cleanPhone}`, "_self");
    }
  };

  const handleMaps = () => {
    const address =
      currentAppointment?.clientDetails?.billingAddress ||
      currentAppointment?.clientDetails?.shippingAddress;
    if (address) {
      const fullAddress = `${address.address1} ${address.address2 || ""} ${
        address.city
      } ${address.province} ${address.zip} ${address.country}`.trim();
      const encodedAddress = encodeURIComponent(fullAddress);
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`,
        "_blank",
      );
    }
  };

  const handleAddOrUpdateFieldStatus = async (record) => {
    if (!appointmentId) return;

    // For new records, assign appointmentId here
    const payload = {
      ...record,
      appointmentId,
      id: record.id || 0, // 0 for new
    };

    try {
      const apiResult = await dispatch(
        addOrUpdateTechnicianTimeRecord(appointmentId, payload),
      );
      // apiResult has saved record - replace in redux:
      if (apiResult) {
        dispatch(addOrUpdateFieldStatus(apiResult));
      }
    } catch (e) {
      console.error("Failed to save technician time record:", e);
    }
  };

  const handleDeleteTimeRecord = (id) => {
    dispatch(deleteTechnicianTimeRecordAPI(id)); // this does the API call and redux update on success
  };
  // Signature handler
  const handleSignatureComplete = (sigData) => {
    dispatch(setSignature(sigData));
    setShowSignatureModal(false);
  };

  // Loading state
  if (appointmentLoading) {
    return (
      <Layout
        title={intl.formatMessage({ id: "Appointment.Details" })}
        onNavigate={onNavigate}
      >
        <div className="p-4 space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    );
  }

  // Error state
  if (appointmentError) {
    return (
      <Layout
        title={intl.formatMessage({ id: "Appointment.Details" })}
        onNavigate={onNavigate}
      >
        <div className="p-4">
          <div className="text-center py-8">
            <h3 className="text-lg font-semibold text-red-600 mb-2">
              <FormattedMessage id="Appointment.Error" />
            </h3>
            <p className="text-gray-600">{appointmentError}</p>
          </div>
        </div>
      </Layout>
    );
  }

  // No appointment found
  if (!currentAppointment) {
    return (
      <Layout
        title={intl.formatMessage({ id: "Appointment.Details" })}
        onNavigate={onNavigate}
      >
        <div className="p-4">
          <div className="text-center py-8">
            <h3 className="text-lg font-semibold mb-2">
              <FormattedMessage id="Appointment.NotFound" />
            </h3>
            <p className="text-gray-600">
              <FormattedMessage id="Appointment.ErrorMessage" />
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      title={intl.formatMessage({ id: "Appointment.Details" })}
      onNavigate={onNavigate}
    >
      <div className="space-y-4">
        <AppointmentHeader onNavigate={onNavigate} />

        <ClientInfoCard
          appointment={currentAppointment}
          onCall={handleCall}
          onMaps={handleMaps}
          status={currentAppointment?.appointmentStatusId}
          onStatusChange={handleStatusChange}
        />

        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full"
        >
          <TabsList className="flex w-full justify-between h-12">
            <TabsTrigger
              value="overview"
              className="flex-1 text-xs sm:text-sm px-1 sm:px-2"
            >
              <FormattedMessage id="Appointment.Overview" />
            </TabsTrigger>
            <TabsTrigger
              value="time"
              className="flex-1 text-xs sm:text-sm px-1 sm:px-2"
            >
              <FormattedMessage id="Appointment.Time" />
            </TabsTrigger>
            <TabsTrigger
              value="equipment"
              className="flex-1 text-xs sm:text-sm px-1 sm:px-2"
            >
              <FormattedMessage id="Appointment.ProductsServices" />
            </TabsTrigger>
            <TabsTrigger
              value="files"
              className="flex-1 text-xs sm:text-sm px-1 sm:px-2"
            >
              <FormattedMessage id="Appointment.Files" />
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <OverviewTab
              appointmentId={appointmentId}
              workNotes={workNotes}
              setWorkNotes={(notes) => dispatch(setWorkNotes(notes))}
              signature={signature}
              customerSignatureName={customerSignatureName}
              customerSignatureEmail={customerSignatureEmail}
              signatureTimestamp={signatureTimestamp}
              onSave={handleSaveNotes}
            />
          </TabsContent>

          <TabsContent value="time" className="space-y-4">
            <TimeTab
              fieldStatus={fieldStatus}
              addOrUpdateField={handleAddOrUpdateFieldStatus}
              removeField={handleDeleteTimeRecord}
            />
          </TabsContent>

          <TabsContent value="equipment" className="space-y-4">
            <EquipmentTab
              inventory={inventory}
              appointmentItems={appointmentItems}
              appointmentId={appointmentId}
            />
          </TabsContent>

          <TabsContent value="files" className="space-y-4">
            <FilesTab appointmentId={appointmentId} />
          </TabsContent>
        </Tabs>
      </div>

      {showSignatureModal && (
        <SignatureModal
          isOpen={showSignatureModal}
          onClose={() => setShowSignatureModal(false)}
          onSignatureComplete={handleSignatureComplete}
          appointmentId={appointmentId?.toString?.() || ""}
          clientName={currentAppointment?.clientName || ""}
        />
      )}
    </Layout>
  );
}

export default AppointmentDetailPage;
